import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { triggerBlobDownload } from '@/services/userExportsService';
import { saveUserExportFromTool } from '@/utils/saveUserExport';

export const ALUNATOOLS_PDF_LOGO = '/images/alunatools/e2b3cf9e-19de-4b54-9306-7b53664ced03.png';
export const ALUNATOOLS_PDF_FOOTER_TEXT = 'Généré avec AlunaTools';

const FOOTER_HEIGHT_MM = 50;

interface LoadedImage {
  dataUrl: string;
  width: number;
  height: number;
}

export interface ExportToPdfOptions {
  element: HTMLElement;
  fileName: string;
  sourceTool: string;
  orientation?: 'portrait' | 'landscape';
  marginMm?: number;
  /** Réduit le contenu pour tenir sur une page (graphiques, etc.) */
  fitSinglePage?: boolean;
}

const resolveAssetUrl = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
};

const loadImageDataUrl = (src: string): Promise<LoadedImage | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = resolveAssetUrl(src);
  });

const getLogoDimensionsMm = (naturalWidth: number, naturalHeight: number) => {
  const maxWidth = 48;
  const maxHeight = 18;
  const ratio = naturalWidth / naturalHeight;

  let width = maxWidth;
  let height = width / ratio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * ratio;
  }

  return { width, height };
};

const addBrandedFooter = (
  pdf: jsPDF,
  pageNumber: number,
  logo: LoadedImage | null,
  marginMm: number
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const footerTop = pageHeight - FOOTER_HEIGHT_MM;

  pdf.setPage(pageNumber);
  pdf.setDrawColor(229, 231, 235);
  pdf.setLineWidth(0.3);
  pdf.line(marginMm, footerTop, pageWidth - marginMm, footerTop);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(107, 114, 128);

  const textY = footerTop + FOOTER_HEIGHT_MM - 12;

  let logoWidth = 0;
  let logoHeight = 0;
  let logoY = footerTop + 10;

  if (logo) {
    const dims = getLogoDimensionsMm(logo.width, logo.height);
    logoWidth = dims.width;
    logoHeight = dims.height;
    logoY = footerTop + 10;
    const logoX = pageWidth / 2 - logoWidth / 2;

    pdf.addImage(logo.dataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
  }

  pdf.text(ALUNATOOLS_PDF_FOOTER_TEXT, pageWidth / 2, textY, { align: 'center' });
};

const sliceCanvas = (
  source: HTMLCanvasElement,
  sourceY: number,
  sourceHeight: number
): string => {
  const slice = document.createElement('canvas');
  slice.width = source.width;
  slice.height = sourceHeight;
  const ctx = slice.getContext('2d');
  if (!ctx) {
    return source.toDataURL('image/jpeg', 0.98);
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(
    source,
    0,
    sourceY,
    source.width,
    sourceHeight,
    0,
    0,
    source.width,
    sourceHeight
  );
  return slice.toDataURL('image/jpeg', 0.98);
};

const addImagePagesToPdf = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  marginMm: number,
  fitSinglePage: boolean
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginMm * 2;
  const availableHeight = pageHeight - marginMm * 2 - FOOTER_HEIGHT_MM;

  let renderWidth = contentWidth;
  let renderHeight = (canvas.height * renderWidth) / canvas.width;

  if (fitSinglePage && renderHeight > availableHeight) {
    const scale = availableHeight / renderHeight;
    renderWidth *= scale;
    renderHeight = availableHeight;
  }

  const xPos = marginMm + (contentWidth - renderWidth) / 2;

  if (renderHeight <= availableHeight) {
    const imgData = canvas.toDataURL('image/jpeg', 0.98);
    pdf.addImage(imgData, 'JPEG', xPos, marginMm, renderWidth, renderHeight);
    return;
  }

  const totalSourceHeight = canvas.height;
  const sourceHeightPerPage = (availableHeight / renderHeight) * totalSourceHeight;
  let sourceY = 0;
  let pageIndex = 0;

  while (sourceY < totalSourceHeight - 1) {
    if (pageIndex > 0) {
      pdf.addPage();
    }

    const remainingSource = totalSourceHeight - sourceY;
    const currentSourceHeight = Math.min(sourceHeightPerPage, remainingSource);
    const currentRenderHeight = (currentSourceHeight / totalSourceHeight) * renderHeight;
    const sliceData = sliceCanvas(canvas, Math.round(sourceY), Math.round(currentSourceHeight));

    pdf.addImage(sliceData, 'JPEG', xPos, marginMm, renderWidth, currentRenderHeight);

    sourceY += currentSourceHeight;
    pageIndex += 1;
  }
};

export async function exportElementToPdf({
  element,
  fileName,
  sourceTool,
  orientation = 'portrait',
  marginMm = 10,
  fitSinglePage = false,
}: ExportToPdfOptions): Promise<{ ok: boolean; error?: string }> {
  const safeName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      width: element.scrollWidth || element.offsetWidth,
      height: element.scrollHeight || element.offsetHeight,
    });

    if (canvas.width === 0 || canvas.height === 0) {
      return { ok: false, error: 'La capture du contenu a échoué (zone vide).' };
    }

    const pdf = new jsPDF({
      orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    addImagePagesToPdf(pdf, canvas, marginMm, fitSinglePage);

    const logo = await loadImageDataUrl(ALUNATOOLS_PDF_LOGO);
    const totalPages = pdf.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      addBrandedFooter(pdf, page, logo, marginMm);
    }

    const blob = pdf.output('blob');
    triggerBlobDownload(blob, safeName);

    const saved = await saveUserExportFromTool({
      blob,
      fileName: safeName,
      fileType: 'PDF',
      sourceTool,
    });

    if (!saved.ok && saved.error) {
      return { ok: true, error: saved.error };
    }

    return { ok: true };
  } catch (error) {
    console.error('[exportToPdf] error:', error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Erreur lors de la génération du PDF',
    };
  }
}

export async function exportElementByIdToPdf(
  elementId: string,
  options: Omit<ExportToPdfOptions, 'element'>
): Promise<{ ok: boolean; error?: string }> {
  const element = document.getElementById(elementId);
  if (!element) {
    return { ok: false, error: 'Élément à exporter introuvable' };
  }

  return exportElementToPdf({ ...options, element });
}

export interface ExportImageToPdfOptions {
  imageDataUrl: string;
  fileName: string;
  sourceTool: string;
  orientation?: 'portrait' | 'landscape';
  marginMm?: number;
  fitSinglePage?: boolean;
}

const loadImageFromDataUrl = (dataUrl: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

export async function exportImageDataUrlToPdf({
  imageDataUrl,
  fileName,
  sourceTool,
  orientation = 'portrait',
  marginMm = 10,
  fitSinglePage = true,
}: ExportImageToPdfOptions): Promise<{ ok: boolean; error?: string }> {
  const safeName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

  try {
    const img = await loadImageFromDataUrl(imageDataUrl);
    if (!img) {
      return { ok: false, error: 'Image invalide pour la génération du PDF.' };
    }

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return { ok: false, error: 'Impossible de préparer l\'image pour le PDF.' };
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    const pdf = new jsPDF({
      orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    addImagePagesToPdf(pdf, canvas, marginMm, fitSinglePage);

    const logo = await loadImageDataUrl(ALUNATOOLS_PDF_LOGO);
    const totalPages = pdf.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      addBrandedFooter(pdf, page, logo, marginMm);
    }

    const blob = pdf.output('blob');
    triggerBlobDownload(blob, safeName);

    const saved = await saveUserExportFromTool({
      blob,
      fileName: safeName,
      fileType: 'PDF',
      sourceTool,
    });

    if (!saved.ok && saved.error) {
      return { ok: true, error: saved.error };
    }

    return { ok: true };
  } catch (error) {
    console.error('[exportImageDataUrlToPdf] error:', error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Erreur lors de la génération du PDF',
    };
  }
}

export async function exportChartPreviewToPdf(
  containerId: string,
  options: Omit<ExportToPdfOptions, 'element'>
): Promise<{ ok: boolean; error?: string }> {
  const element = document.getElementById(containerId);
  if (!element) {
    return { ok: false, error: 'Graphique introuvable' };
  }

  const chartArea = element.querySelector('.tabliwizz-chart-area') as HTMLElement | null;
  const previousContainerMinHeight = element.style.minHeight;
  const previousChartHeight = chartArea?.style.height ?? '';

  element.style.minHeight = '440px';
  if (chartArea) {
    chartArea.style.height = '360px';
  }

  window.dispatchEvent(new Event('resize'));
  await new Promise((resolve) => setTimeout(resolve, 500));

  const svg = element.querySelector('svg.recharts-surface');
  if (!svg) {
    element.style.minHeight = previousContainerMinHeight;
    if (chartArea) chartArea.style.height = previousChartHeight;
    return { ok: false, error: 'Aucun graphique à exporter. Sélectionnez des données dans le tableau.' };
  }

  const result = await exportElementToPdf({
    ...options,
    element,
    fitSinglePage: true,
    marginMm: 12,
  });

  element.style.minHeight = previousContainerMinHeight;
  if (chartArea) {
    chartArea.style.height = previousChartHeight;
  }
  window.dispatchEvent(new Event('resize'));

  return result;
}
